import { PureComponent, ReactNode } from 'react';
import { captureErrorString } from '../../pages/captureError';
import { Patient } from '../../store/types/patient';
import CompleteCr from './CompleteCr';
import CreateCr from './CreateCr';
import { crossIcon } from './Icons';
import Modal from './Modal';
import ModalDialog from './ModalDialog';
import SelectCr from './SelectCr';
import pushMedicalCertificate from '../../api/pushMedicalCertificate';
import pushForm from '../../api/pushForm';
import AppLoader from './AppLoader';

type Props = {
    patient: Patient;
    closeMe: () => void;
    zIndex?: number;
    defaultState?: Partial<State>;
    clinicId?: string;
    customCertificateContent?: string;
    isMarkdown?: boolean;
    docid: string;
};

type Page = 'select-cr' | 'create-cr' | 'complete';

type State = {
    selectedCertificate?: string;
    content?: string;
    originalContent?: string;
    heading?: string;
    withPrintRxConfig?: boolean;
    params?: { [k: string]: string };
    page: Page;
    type?: 'mcert' | 'consent';
    isReady: boolean;
};

let script: HTMLScriptElement | undefined;

export const loadTinyMCE = async (): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
        // TODO
        // - host on the server
        // - get API key
        if (!script) {
            const element = document.querySelector('body');
            script = document.createElement('script');
            script.src = '/tinymce.js';
            script.onload = () => resolve();
            script.onerror = () => {
                script = undefined;
                captureErrorString('Failed to load TinyMCE script');
            };
            element?.appendChild(script);
        } else {
            resolve();
        }
    });
};

class MedicalCertificateDialog extends PureComponent<Props, State> {
    state: State = {
        selectedCertificate: undefined,
        page: 'select-cr',
        ...this.props.defaultState,
        isReady: false,
    };

    cIcon = crossIcon('w-14 h-13 fill-neutral-1 cursor-pointer');

    onContinue = (id: string, type: 'mcert' | 'consent'): void => {
        this.setState({ page: 'create-cr', selectedCertificate: id, type });
    };

    componentDidMount = (): void => {
        loadTinyMCE().then(() => this.setState({ isReady: true }));
    };

    setContent = (
        content: string,
        originalContent: string,
        heading: string,
        withPrintRxConfig: boolean,
        params?: { [k: string]: string },
    ): void => {
        this.setState({
            page: 'complete',
            content: content,
            originalContent,
            heading,
            withPrintRxConfig,
            params,
        });
    };

    closeMe = (): void => {
        const { closeMe } = this.props;
        closeMe();
    };

    render(): ReactNode {
        const { patient, clinicId, docid } = this.props;
        const {
            page,
            selectedCertificate,
            content,
            originalContent,
            heading,
            withPrintRxConfig,
            params,
            type,
            isReady,
        } = this.state;

        return (
            <ModalDialog>
                <Modal closeMe={this.closeMe} contentCssClasses={'w-60p h-5/6'}>
                    <div className="flex flex-col bg-white font-lato font-400 text-14 shadow-100 h-full">
                        <div className="flex items-center justify-between p-16 bg-darwin-neutral-50">
                            <p className="font-700 text-16">Create a medical document</p>
                            <div onClick={this.closeMe}>{this.cIcon}</div>
                        </div>
                        {!isReady ? (
                            <AppLoader />
                        ) : page === 'select-cr' ? (
                            <SelectCr onContinue={this.onContinue} />
                        ) : page === 'create-cr' && selectedCertificate ? (
                            <CreateCr
                                patient={patient}
                                certId={selectedCertificate}
                                onContinue={this.setContent}
                                defaultHeading={heading}
                                customContent={this.props.customCertificateContent}
                                isMarkdown={this.props.isMarkdown}
                            />
                        ) : page === 'complete' &&
                          content &&
                          selectedCertificate &&
                          originalContent &&
                          type ? (
                            <CompleteCr
                                content={content}
                                patient={patient}
                                certId={selectedCertificate}
                                onContinue={this.closeMe}
                                originalContent={originalContent}
                                heading={heading}
                                withPrintRxConfig={withPrintRxConfig}
                                params={params}
                                pushApi={type === 'consent' ? pushForm : pushMedicalCertificate}
                                type={type}
                                clinicId={clinicId}
                                docid={docid}
                            />
                        ) : null}
                    </div>
                </Modal>
            </ModalDialog>
        );
    }
}

export default MedicalCertificateDialog;
